import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  TextField,
  Typography,
} from '@mui/material'
import { Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material'
import {
  StyledButton,
  StyledDialog,
  StyledDialogContent,
  StyledDialogTitle,
} from '../../styles/StyledComponents'
import type { CompanyWithStats } from '../../types'

export type KeptAccountReason = 'acting_admin' | 'platform_admin' | 'other_company_member'

export type CompanyDeletionStep =
  'started' | 'stripe' | 'billing_jobs' | 'members' | 'company_data' | 'tombstone'

/** Progress of a deletion that stopped part way; `null` on the preview when none is recorded. */
export interface CompanyDeletionProgress {
  inProgress: boolean
  startedAt: string | null
  requestedBy: { userId: string; name: string } | null
  reason: string | null
  lastStep: CompanyDeletionStep | null
  lastError: string | null
  lastErrorAt: string | null
  stripeCancelled: boolean
}

/** Shape of `GET /api/company/:companyId/removal-preview` (server contract, SC-409). */
export interface CompanyRemovalPreview {
  success?: boolean
  company?: {
    id: string
    name: string
    domain?: string
    status?: string
    billing_tier?: string
    created_at?: string
  }
  impact: {
    usersToUnlink?: number
    roomsToRemove?: number
    hasActiveSubscription?: boolean
    stripeCustomerId?: string | null
    stripeSubscriptionId?: string | null
    accountsToDelete?: number
    accountsToKeep?: Array<{
      userId: string
      name?: string
      email?: string
      reason: KeptAccountReason
    }>
    toDelete?: Record<string, number>
    preserved?: string[]
    walletBalance?: number | null
    walletCurrency?: string | null
  }
  warning?: string
  deletion?: CompanyDeletionProgress | null
}

interface RemoveCompanyDialogProps {
  open: boolean
  company: CompanyWithStats | null
  preview: CompanyRemovalPreview | null
  previewLoading: boolean
  previewError: string | null
  reason: string
  onReasonChange: (reason: string) => void
  removing: boolean
  onClose: () => void
  onConfirm: () => void
}

const KEPT_REASON_LABEL: Record<KeptAccountReason, string> = {
  acting_admin: 'you, the acting admin',
  platform_admin: 'platform admin',
  other_company_member: 'also a member of another company',
}

const DELETION_STEP_LABEL: Record<CompanyDeletionStep, string> = {
  started: 'Marking the company for deletion and disconnecting its rooms',
  stripe: 'Cancelling the Stripe subscription',
  billing_jobs: 'Cancelling pending billing items',
  members: 'Removing member accounts',
  company_data: 'Deleting rooms, bookings, invites, templates and chats',
  tombstone: 'Finalising the company record',
}

function stepLabel(step: string | null | undefined): string {
  if (!step) return 'Unknown step'
  // A step added on the server before this bundle knows it: the raw key beats saying "unknown".
  return DELETION_STEP_LABEL[step as CompanyDeletionStep] ?? step
}

function formatTimestamp(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString()
}

// The server falls back to the email when an account has no first or last name; repeating it in
// brackets would only add noise.
function keptAccountLabel(account: { userId: string; name?: string; email?: string }): string {
  const name = account.name?.trim()
  const email = account.email?.trim()
  if (!name) return email || account.userId
  return email && email.toLowerCase() !== name.toLowerCase() ? `${name} (${email})` : name
}

function count(toDelete: Record<string, number> | undefined, ...keys: string[]): number {
  return keys.reduce((sum, key) => sum + (Number(toDelete?.[key]) || 0), 0)
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

function formatMoney(amount: number, currency: string | null | undefined): string {
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount)
    } catch {
      // Unknown currency code from billing data: show the raw code rather than nothing.
    }
  }
  return `${amount.toFixed(2)}${currency ? ` ${currency}` : ''}`
}

const ImpactRow: React.FC<{ label: string; value: React.ReactNode; testId?: string }> = ({
  label,
  value,
  testId,
}) => (
  <Box
    data-testid={testId}
    sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}
  >
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
)

const StatusLine: React.FC<{ testId: string; children: React.ReactNode }> = ({
  testId,
  children,
}) => (
  <Typography variant="body2" data-testid={testId} sx={{ color: 'text.primary' }}>
    {children}
  </Typography>
)

/**
 * Where a stopped deletion stands. The admin decided (SC-409) that a half-deleted company is
 * finished rather than restored, so before confirming they must see exactly what already
 * happened, including the Stripe state, because that is the step that most often stops a run.
 */
const DeletionStatusBlock: React.FC<{
  deletion: CompanyDeletionProgress | null
  hasActiveSubscription: boolean
}> = ({ deletion, hasActiveSubscription }) => {
  const startedAt = formatTimestamp(deletion?.startedAt)
  const requestedBy = deletion?.requestedBy?.name || deletion?.requestedBy?.userId
  const lastErrorAt = formatTimestamp(deletion?.lastErrorAt)

  let stripeLine = 'Stripe subscription: no active subscription'
  if (deletion?.stripeCancelled) {
    stripeLine = 'Stripe subscription: already cancelled'
  } else if (hasActiveSubscription) {
    stripeLine =
      'Stripe subscription: not yet cancelled. It will be cancelled immediately, with no refund'
  }

  return (
    <Alert
      severity="warning"
      data-testid="remove-company-deletion-status"
      sx={{ mb: 2, '& .MuiAlert-message': { display: 'grid', gap: 0.5 } }}
    >
      <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 700 }}>
        This company&apos;s deletion is incomplete.
      </Typography>
      <StatusLine testId="remove-company-deletion-started">
        Started: {startedAt ?? 'time unknown'}
        {requestedBy ? ` by ${requestedBy}` : ''}
      </StatusLine>
      {deletion?.reason && (
        <StatusLine testId="remove-company-deletion-reason">Reason: {deletion.reason}</StatusLine>
      )}
      <StatusLine testId="remove-company-deletion-step">
        Stopped during: {stepLabel(deletion?.lastStep)}
      </StatusLine>
      {deletion?.lastError && (
        <StatusLine testId="remove-company-deletion-error">
          Last error: {deletion.lastError}
          {lastErrorAt ? ` (${lastErrorAt})` : ''}
        </StatusLine>
      )}
      <StatusLine testId="remove-company-deletion-stripe">{stripeLine}</StatusLine>
      <StatusLine testId="remove-company-deletion-irreversible">
        Accounts and data already removed can&apos;t be restored.
      </StatusLine>
    </Alert>
  )
}

/**
 * Confirmation for permanently deleting a company. Every number comes from the server preview so
 * the admin confirms exactly what the server will do; without a preview there is nothing honest to
 * show, so Confirm stays disabled rather than opening an empty warning.
 */
export const RemoveCompanyDialog: React.FC<RemoveCompanyDialogProps> = ({
  open,
  company,
  preview,
  previewLoading,
  previewError,
  reason,
  onReasonChange,
  removing,
  onClose,
  onConfirm,
}) => {
  const impact = preview?.impact
  const toDelete = impact?.toDelete
  const accountsToDelete = Number(impact?.accountsToDelete) || 0
  const accountsToKeep = impact?.accountsToKeep ?? []
  const walletBalance = typeof impact?.walletBalance === 'number' ? impact.walletBalance : null
  // Either signal is enough: the card's status can be stale, and an older server sends no
  // `deletion`, so neither alone may hide that the company is already half deleted.
  const finishing =
    preview?.deletion?.inProgress === true || (company?.status as string) === 'deleting'
  const canConfirm = !!preview && !previewLoading && !previewError && !removing

  return (
    <StyledDialog open={open} onClose={removing ? undefined : onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <WarningIcon sx={{ color: 'error.main', fontSize: 24 }} />
        {finishing
          ? `Finish deleting ${company?.name ?? ''}`
          : `Delete company: ${company?.name ?? ''}`}
      </StyledDialogTitle>
      <StyledDialogContent>
        {previewLoading && (
          <Box
            data-testid="remove-company-preview-loading"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Checking what will be deleted...
            </Typography>
          </Box>
        )}

        {!previewLoading && previewError && (
          <Alert severity="error" data-testid="remove-company-preview-error" sx={{ mb: 2 }}>
            Could not load what this deletion would affect: {previewError}. Nothing has been
            deleted. Close this dialog and try again.
          </Alert>
        )}

        {!previewLoading && !previewError && preview && finishing && (
          <DeletionStatusBlock
            deletion={preview.deletion ?? null}
            hasActiveSubscription={!!impact?.hasActiveSubscription}
          />
        )}

        {!previewLoading && !previewError && impact && (
          <Box data-testid="remove-company-impact" sx={{ mb: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>This is permanent and cannot be undone.</strong> Anyone in the company&apos;s
              rooms now will be disconnected.
            </Alert>

            {finishing && (
              <Typography
                variant="subtitle1"
                data-testid="remove-company-remaining-heading"
                sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}
              >
                What is left to delete
              </Typography>
            )}

            <Box
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'action.hover',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle1"
                data-testid="remove-company-accounts-deleted"
                sx={{ color: 'error.main', fontWeight: 600 }}
              >
                {plural(accountsToDelete, 'member account', 'member accounts')} will be{' '}
                <strong>permanently deleted</strong>.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Those people will no longer be able to sign in. No email is sent to them.
              </Typography>

              {accountsToKeep.length > 0 && (
                <Box sx={{ mt: 1.5 }} data-testid="remove-company-accounts-kept">
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    These accounts are kept and only removed from the company:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 3, color: 'text.primary' }}>
                    {accountsToKeep.map((account) => (
                      <li key={account.userId}>
                        <Typography variant="body2">
                          {keptAccountLabel(account)} -{' '}
                          {KEPT_REASON_LABEL[account.reason] ?? account.reason}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                p: 2,
                mb: 2,
                bgcolor: 'action.hover',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1 }}>
                Also deleted
              </Typography>
              {/* SC-422: the company's rooms, lobby included. Virtual rooms belong to the map and
                  are shared with other companies, so they are never deleted with a company. */}
              <ImpactRow
                testId="remove-company-rooms"
                label="Rooms"
                value={count(toDelete, 'ColyseusRoom')}
              />
              <ImpactRow
                testId="remove-company-bookings"
                label="Room bookings"
                value={count(toDelete, 'RoomBooking')}
              />
              <ImpactRow
                testId="remove-company-invites"
                label="Room invites"
                value={count(toDelete, 'RoomInvite')}
              />
              <ImpactRow
                testId="remove-company-invite-links"
                label="Workspace invite links"
                value={count(toDelete, 'CompanyInviteLink')}
              />
              <ImpactRow
                testId="remove-company-templates"
                label="Email templates"
                value={count(toDelete, 'CompanyEmailTemplate')}
              />
              <ImpactRow
                testId="remove-company-chats"
                label="Chat conversations"
                value={`${count(toDelete, 'Conversation')} (${plural(
                  count(toDelete, 'DirectMessage'),
                  'message',
                  'messages'
                )})`}
              />
              <ImpactRow label="Memberships" value={count(toDelete, 'CompanyUser')} />
            </Box>

            <Box
              sx={{
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 1 }}>
                Billing
              </Typography>
              {/* In finish mode the status block already states the Stripe position; a second,
                  differently worded line here could read as a contradiction. */}
              {finishing ? null : impact.hasActiveSubscription ? (
                <Typography
                  variant="body2"
                  data-testid="remove-company-stripe"
                  sx={{ color: 'error.main', mb: 1 }}
                >
                  The Stripe subscription will be cancelled immediately, with no refund.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  No active Stripe subscription.
                </Typography>
              )}
              {walletBalance !== null && walletBalance > 0 ? (
                <Alert severity="warning" data-testid="remove-company-wallet" sx={{ mb: 1 }}>
                  The wallet balance of {formatMoney(walletBalance, impact.walletCurrency)} is kept
                  on the billing account. It is not refunded.
                </Alert>
              ) : (
                <Typography
                  variant="body2"
                  data-testid="remove-company-wallet"
                  sx={{ color: 'text.secondary', mb: 1 }}
                >
                  {walletBalance === null
                    ? 'No wallet.'
                    : `Wallet balance (${formatMoney(walletBalance, impact.walletCurrency)}) is kept.`}
                </Typography>
              )}
              <Typography
                variant="body2"
                data-testid="remove-company-preserved"
                sx={{ color: 'text.secondary' }}
              >
                Billing records and logs are kept for audit.
                {impact.preserved && impact.preserved.length > 0
                  ? ` (${impact.preserved.join(', ')})`
                  : ''}
              </Typography>
            </Box>

            {preview?.warning && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {preview.warning}
              </Alert>
            )}
          </Box>
        )}

        {/* A re-run keeps the first run's reason (shown in the status block), so a field here
            would only collect input the server discards. */}
        {!finishing && (
          <TextField
            margin="dense"
            label="Reason for deletion (optional)"
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            disabled={removing}
            placeholder="Enter the reason for deleting this company..."
          />
        )}
      </StyledDialogContent>
      <DialogActions
        sx={{ px: 3, py: 2, bgcolor: 'background.default', borderTop: 1, borderColor: 'divider' }}
      >
        <StyledButton onClick={onClose} variant="outlined" disabled={removing}>
          Cancel
        </StyledButton>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={!canConfirm}
          data-testid="remove-company-confirm"
          startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          {removing ? 'Deleting...' : finishing ? 'Finish deletion' : 'Delete permanently'}
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}
